import { useEffect, useState, useMemo, memo } from 'react';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Rnd } from 'react-rnd';
import * as maplibregl from 'maplibre-gl';
import { Image, Button, Tooltip, message } from 'antd';
import {
  FileTextOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  CopyOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { PiCheckBold, PiXBold } from 'react-icons/pi';
import { CollectItemFileAPIs } from '~/common/typescript/apis/CollectItemFileAPIs';
import { CollectFormAPIs, CollectForm_GetOne_Response } from '~/common/typescript/apis/CollectFormAPIs';
import { CollectItemAPIs } from '~/common/typescript/apis/CollectItemAPIs';
import { API_URL_ADMIN } from '~/config/config';
import {
  allCollectFormsMapAtom,
  collectFormFieldsMapAtom,
  singleFormsCacheAtom,
} from '../../panels/GeocollectDatatablePanel/atoms/CollectFormAtoms';
import { projectMembersMapAtom } from '../../Member/MemberStore';
import { parseFormContent, ParsedFormField } from '../../panels/GeocollectDatatablePanel/utils/CollectFormParser';
import DateTimeViewer from '~/components/common/DateTimeViewer';
import FieldTypeIcon from '../../panels/GeocollectDatatablePanel/utils/FieldTypeIcon';
import CollectFormDetailDrawer from './CollectFormDetailDrawer';
import MapUtils from '../../utils/MapUtils';

export interface CollectItemCandidate {
  title: string;
  properties: any;
  geometry?: any;
}

export interface CollectItemPopupProps {
  title: string;
  lngLat?: maplibregl.LngLat;
  properties: any;
  geometry?: any;
  items?: CollectItemCandidate[];
  selectedIndex?: number;
}

// Global function to be set by component
let globalShowCollectItemPopup: ((props: CollectItemPopupProps) => void) | null = null;

export const showCollectItemPopup = (props: CollectItemPopupProps) => {
  if (globalShowCollectItemPopup) {
    globalShowCollectItemPopup(props);
  } else {
    console.warn('CollectItemPopup not initialized yet');
  }
};

import { atom } from 'jotai';

export const collectItemPopupPropsAtom = atom<CollectItemPopupProps | null>(null);

// Atom for GeocollectItemProperties mode
export type GeocollectItemPropertiesMode = 'VIEW' | 'EDIT';
export const geocollectItemPropertiesModeAtom = atom<GeocollectItemPropertiesMode>('VIEW');

// Atom to trigger refresh of collect items data table
export const refreshCollectItemsAtom = atom<number>(0);
export const triggerRefreshCollectItems = atom(
  null,
  (get, set) => {
    set(refreshCollectItemsAtom, get(refreshCollectItemsAtom) + 1);
  }
);

const CollectItemPopup = () => {
  const { t } = useTranslation();
  const [props, setProps] = useAtom(collectItemPopupPropsAtom);
  const allFormsMap = useAtomValue(allCollectFormsMapAtom);
  const formFieldsMap = useAtomValue(collectFormFieldsMapAtom);
  const projectMembersMap = useAtomValue(projectMembersMapAtom);
  const setSingleFormsCache = useSetAtom(singleFormsCacheAtom);

  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [currentPreviewImage, setCurrentPreviewImage] = useState<any>(null);
  const [collectFormData, setCollectFormData] = useState<CollectForm_GetOne_Response | null>(null);
  const [collectFormName, setCollectFormName] = useState<string>('');
  const [loadingForm, setLoadingForm] = useState(false);
  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false);
  const [resolvedFormUuid, setResolvedFormUuid] = useState<string>('');
  const [serverProperties, setServerProperties] = useState<Record<string, any> | null>(null);

  const items = props?.items && props.items.length > 0 ? props.items : null;
  const currentIndex = props?.selectedIndex ?? 0;
  const totalItems = items ? items.length : 1;

  const displayProperties = useMemo(() => {
    if (!props?.properties && !serverProperties) return {};
    return {
      ...(props?.properties || {}),
      ...(serverProperties || {}),
    };
  }, [props?.properties, serverProperties]);

  const currentFormUuid = displayProperties?.collect_form_uuid || resolvedFormUuid;
  const currentFormFromMap = currentFormUuid ? allFormsMap[currentFormUuid] : null;
  const activeFormData = collectFormData || currentFormFromMap;
  const activeFormName = collectFormName || currentFormFromMap?.name || '';

  const hasCoords = props?.lngLat && typeof props.lngLat.lat === 'number' && typeof props.lngLat.lng === 'number';

  const handleSelectCandidate = (nextIndex: number) => {
    if (!props || !items || items.length <= 1) return;
    const normalizedIndex = ((nextIndex % items.length) + items.length) % items.length;
    const candidate = items[normalizedIndex];
    if (!candidate) return;
    setServerProperties(null);
    setProps({
      ...props,
      selectedIndex: normalizedIndex,
      title: candidate.title,
      properties: candidate.properties,
      geometry: candidate.geometry,
    });
    if (candidate.geometry && props.lngLat) {
      MapUtils.highlightFeatureCollection({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: candidate.properties,
            geometry: candidate.geometry,
          },
        ],
      } as any);
    }
  };

  // Calculate initial position near clicked point on map (avoiding the 400px GeocollectItemPanel on the right)
  useEffect(() => {
    const panelWidth = 350;
    const panelHeight = 460;
    const rightPanelReserved = 420;
    const defaultX = Math.max(20, window.innerWidth - panelWidth - rightPanelReserved);

    if (!props?.lngLat || !window.map) {
      if (props && !position) {
        setPosition({
          x: defaultX,
          y: 90,
        });
      }
      return;
    }

    try {
      const point = window.map.project(props.lngLat);
      let x = point.x + 20;
      let y = point.y - 80;

      if (x + panelWidth > window.innerWidth - rightPanelReserved) {
        x = Math.max(20, Math.min(point.x - panelWidth - 20, defaultX));
      }
      if (y + panelHeight > window.innerHeight - 20) {
        y = Math.max(70, window.innerHeight - panelHeight - 20);
      }
      if (y < 70) y = 70;

      setPosition({ x, y });
    } catch {
      setPosition({
        x: defaultX,
        y: 90,
      });
    }
  }, [props?.properties?.uuid, props?.lngLat]);

  // Get field definitions for the active form
  const fieldsMap = useMemo(() => {
    if (!currentFormUuid) return {};
    if (formFieldsMap[currentFormUuid]) return formFieldsMap[currentFormUuid];
    if (activeFormData) {
      const parsed = parseFormContent(activeFormData);
      if (parsed?.fields) {
        const map: Record<string, ParsedFormField> = {};
        parsed.fields.forEach((f) => {
          if (f.key) map[f.key] = f;
        });
        return map;
      }
    }
    return {};
  }, [currentFormUuid, formFieldsMap, activeFormData]);

  // Order property keys based on form definition if available
  const orderedPropertyKeys = useMemo(() => {
    const propsObj = displayProperties;
    if (!propsObj || Object.keys(propsObj).length === 0) {
      if (!activeFormData) return [];
    }
    const allKeys = Object.keys(propsObj || {}).filter(
      (k) => k !== 'uuid' && k !== 'collect_form_uuid' && !(propsObj.name && k === 'name')
    );

    if (!activeFormData) return allKeys;

    const parsed = parseFormContent(activeFormData);
    const formFieldKeys = (parsed?.fields || [])
      .map((f) => f.key)
      .filter(
        (k): k is string =>
          Boolean(k) &&
          k !== 'uuid' &&
          k !== 'collect_form_uuid' &&
          !(propsObj.name && k === 'name')
      );

    const ordered: string[] = [];
    formFieldKeys.forEach((k) => {
      if (!ordered.includes(k)) {
        ordered.push(k);
      }
    });

    // Append any extra keys not defined in form schema
    allKeys.forEach((k) => {
      if (!ordered.includes(k)) {
        ordered.push(k);
      }
    });

    return ordered;
  }, [displayProperties, activeFormData]);

  const getPropertyLabel = (key: string, fieldDef?: ParsedFormField) => {
    if (fieldDef?.label) return fieldDef.label;
    if (key === 'user_id' || key === 'collector_id') {
      return t('collect__collector', 'Người thu thập');
    }
    if (key === 'created_at_ts' || key === 'created_at') {
      return t('collect__created_at', 'Thời gian thu thập');
    }
    return key;
  };

  const renderPropertyValue = (key: string, value: any, fieldDef?: ParsedFormField) => {
    if (value === null || value === undefined) return '-';

    if (key === 'user_id' || key === 'collector_id') {
      const numId = Number(value);
      const user = !isNaN(numId) ? projectMembersMap[numId] : null;
      if (user) {
        return (
          <span style={{ fontWeight: 500, color: '#1e293b' }}>
            {user.fullname || user.username || `User #${value}`}
          </span>
        );
      }
      return `User #${value}`;
    }

    if (key === 'created_at_ts') {
      const sec = Number(value);
      const ms = !isNaN(sec) ? (sec < 100000000000 ? sec * 1000 : sec) : value;
      return <DateTimeViewer date={ms} initialMode="relative" />;
    }

    const type = (fieldDef?.type || '').toLowerCase();
    const isDate =
      type === 'date' ||
      type === 'datetime' ||
      type === 'time' ||
      key.toLowerCase().includes('date') ||
      key.toLowerCase().includes('time') ||
      key.toLowerCase().endsWith('_at') ||
      key.toLowerCase().endsWith('_ts');

    if (isDate) {
      const numVal = Number(value);
      let dateVal = value;
      if (!isNaN(numVal) && numVal > 0) {
        dateVal = numVal < 100000000000 ? numVal * 1000 : numVal;
      }
      return <DateTimeViewer date={dateVal} initialMode="relative" />;
    }

    if (typeof value === 'boolean' || type === 'boolean') {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {value ? (
            <>
              <PiCheckBold style={{ color: 'var(--color-success, #22c55e)' }} /> Có
            </>
          ) : (
            <>
              <PiXBold style={{ color: 'var(--color-danger, #ef4444)' }} /> Không
            </>
          )}
        </span>
      );
    }

    const isNumber =
      typeof value === 'number' ||
      type === 'number' ||
      type === 'integer' ||
      type === 'float' ||
      type === 'double' ||
      type === 'decimal' ||
      type === 'numeric';

    if (isNumber) {
      const numVal = typeof value === 'number' ? value : Number(value);
      const formatted =
        !isNaN(numVal) && String(value).trim() !== ''
          ? numVal.toLocaleString('vi-VN')
          : String(value);
      return (
        <span
          style={{
            fontFamily: 'monospace',
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'right',
            display: 'inline-block',
            width: '100%',
          }}
        >
          {formatted}
        </span>
      );
    }

    return String(value);
  };

  // Set global function on mount
  useEffect(() => {
    globalShowCollectItemPopup = setProps;
    (window as any).showCollectItemPopup = setProps;
    return () => {
      globalShowCollectItemPopup = null;
      delete (window as any).showCollectItemPopup;
    };
  }, [setProps]);

  // Fetch form data & full collect item (including full unclipped geometry) when popup opens or selected item changes
  useEffect(() => {
    let isCancelled = false;
    const formUuid = props?.properties?.collect_form_uuid;
    const itemUuid = props?.properties?.uuid;

    if (!props) {
      setCollectFormData(null);
      setCollectFormName('');
      setResolvedFormUuid('');
      setServerProperties(null);
      setIsFormDrawerOpen(false);
      return;
    }

    setServerProperties(null);

    const loadForm = (fUuid: string) => {
      setResolvedFormUuid(fUuid);

      // Check if already in cache
      if (allFormsMap[fUuid]) {
        setCollectFormData(allFormsMap[fUuid]);
        setCollectFormName(allFormsMap[fUuid].name || '');
        return;
      }

      setLoadingForm(true);
      CollectFormAPIs.remote_getOne(fUuid)
        .then((formData) => {
          if (!isCancelled) {
            setCollectFormData(formData);
            setCollectFormName(formData.name || '');
            setSingleFormsCache((prev) => ({ ...prev, [fUuid]: formData }));
          }
        })
        .catch((error) => {
          console.error('Error fetching collect form:', error);
          if (!isCancelled) {
            setCollectFormName('');
          }
        })
        .finally(() => {
          if (!isCancelled) {
            setLoadingForm(false);
          }
        });
    };

    if (formUuid) {
      loadForm(formUuid);
    }

    if (itemUuid) {
      // Always fetch full collect item from server to get unclipped geometry and complete properties
      CollectItemAPIs.remote_getOne(itemUuid)
        .then((item) => {
          if (isCancelled || !item) return;

          if (!formUuid && item.collect_form_uuid) {
            loadForm(item.collect_form_uuid);
          }

          if (item.properties && typeof item.properties === 'object') {
            setServerProperties(item.properties);
          }

          // Highlight full geometry from server when map popup is active
          const rawGeom = item.geom || (item as any).geom_data || (item as any).geometry;
          if (rawGeom && props?.lngLat) {
            try {
              const parsedGeom = typeof rawGeom === 'string' ? JSON.parse(rawGeom) : rawGeom;
              if (parsedGeom && typeof parsedGeom === 'object') {
                MapUtils.highlightFeatureCollection(parsedGeom);
              }
            } catch {
              // Ignore non-JSON geometry strings
            }
          }
        })
        .catch((err) => {
          console.error('Error fetching collect item details and full geometry:', err);
        });
    }

    return () => {
      isCancelled = true;
    };
  }, [props?.properties?.uuid, props?.properties?.collect_form_uuid, props?.lngLat, allFormsMap, setSingleFormsCache]);

  // Fetch images when popup opens
  useEffect(() => {
    const itemUuid = props?.properties?.uuid;
    const formUuid = props?.properties?.collect_form_uuid || resolvedFormUuid;

    if (itemUuid && formUuid) {
      setLoading(true);
      setImages([]);

      // Step 1: Get list of UUIDs
      CollectItemFileAPIs.remote_getPageSyncable({
        collect_item_uuid: itemUuid,
        page: 1,
        pageSize: 20,
      })
        .then((response) => {
          if (response.results && response.results.length > 0) {
            // Step 2: Extract UUIDs and fetch detailed information
            const uuids = response.results.map((item: any) => item.uuid);

            return CollectItemFileAPIs.remote_getListWithUUIDs({
              collect_form_uuid: formUuid,
              uuids: uuids,
            });
          }
          return [];
        })
        .then((detailedFiles) => {
          if (detailedFiles && detailedFiles.length > 0) {
            // Map the detailed results to image objects
            const imageFiles = detailedFiles.map((file: any) => ({
              uuid: file.uuid,
              url: `${API_URL_ADMIN}/api/geocollect/restore/download-file/${file.uuid}`,
              name: file.name || file.uuid.split('__')[1] || file.uuid,
              user: file.user,
              createdAt: file.created_at,
              updatedAt: file.updated_at,
              metadata: file.metadata,
            }));

            setImages(imageFiles);
          }
        })
        .catch((error) => {
          console.error('Error fetching collect item files:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [props?.properties?.uuid, props?.properties?.collect_form_uuid, resolvedFormUuid]);

  // Close panel
  const handleClose = () => {
    setProps(null);
    setImages([]);
    setCollectFormData(null);
    setCollectFormName('');
    setResolvedFormUuid('');
    setServerProperties(null);
    setIsFormDrawerOpen(false);
    setPreviewVisible(false);
    MapUtils.clearHighlight();
  };

  // Copy coordinates
  const handleCopyCoords = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasCoords || !props?.lngLat) return;
    const text = `${props.lngLat.lat.toFixed(6)}, ${props.lngLat.lng.toFixed(6)}`;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        message.success('Đã sao chép tọa độ vào clipboard');
      })
      .catch(() => {
        message.error('Không thể sao chép tọa độ');
      });
  };

  if (!props || !props.lngLat) return null;

  return (
    <>
      <Rnd
        position={position || { x: Math.max(20, window.innerWidth - 390), y: 90 }}
        onDragStop={(e, d) => {
          setPosition({ x: d.x, y: d.y });
        }}
        dragHandleClassName="draggable-header"
        enableResizing={false}
        bounds="window"
        style={{ zIndex: 1000 }}
      >
        <UI_PanelWrapper>
          {/* Draggable Header */}
          <UI_Header className="draggable-header">
            <UI_HeaderLeft>
              <UI_FormBadge
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFormDrawerOpen(true);
                }}
                title="Nhấn để xem thông tin biểu mẫu"
              >
                <FileTextOutlined style={{ marginRight: 4, fontSize: 11 }} />
                <span>Biểu mẫu{activeFormName ? `: ${activeFormName}` : ''}</span>
              </UI_FormBadge>
              <UI_Title title={displayProperties?.name || props?.title || 'Dữ liệu thu thập'}>
                {displayProperties?.name || props?.title || 'Dữ liệu thu thập'}
              </UI_Title>
            </UI_HeaderLeft>
            <UI_HeaderRight>
              {totalItems > 1 && (
                <UI_Pager data-testid="collect-popup-pager">
                  <UI_PagerButton
                    type="button"
                    data-testid="collect-popup-prev"
                    title="Đối tượng trước"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectCandidate(currentIndex - 1);
                    }}
                  >
                    <LeftOutlined style={{ fontSize: 10 }} />
                  </UI_PagerButton>
                  <UI_PagerText data-testid="collect-popup-page-indicator">
                    {currentIndex + 1}/{totalItems}
                  </UI_PagerText>
                  <UI_PagerButton
                    type="button"
                    data-testid="collect-popup-next"
                    title="Đối tượng tiếp theo"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectCandidate(currentIndex + 1);
                    }}
                  >
                    <RightOutlined style={{ fontSize: 10 }} />
                  </UI_PagerButton>
                </UI_Pager>
              )}
              <UI_CloseButton onClick={handleClose} title="Đóng panel">
                <CloseOutlined style={{ fontSize: 12 }} />
              </UI_CloseButton>
            </UI_HeaderRight>
          </UI_Header>

          {/* Panel Body */}
          <UI_Body>
            {/* Images Section */}
            {images.length > 0 && (
              <UI_ImagesSection>
                <UI_SectionTitle>Ảnh thu thập ({images.length})</UI_SectionTitle>
                <Image.PreviewGroup
                  preview={{
                    onChange: (current) => {
                      setCurrentPreviewImage(images[current]);
                    },
                    onVisibleChange: (visible) => {
                      setPreviewVisible(visible);
                      if (visible && images.length > 0) {
                        setCurrentPreviewImage(images[0]);
                      } else {
                        setCurrentPreviewImage(null);
                      }
                    },
                  }}
                >
                  <UI_ImageGrid>
                    {images.map((image, index) => (
                      <UI_ImageItem key={image.uuid || index}>
                        <UI_ImageWrapper>
                          <Image
                            width={76}
                            height={76}
                            style={{ objectFit: 'cover', borderRadius: '4px' }}
                            src={image.url}
                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                          />
                        </UI_ImageWrapper>
                        <UI_ImageName title={image.name}>
                          {image.name?.length > 14 ? image.name.substring(0, 14) + '...' : image.name}
                        </UI_ImageName>
                      </UI_ImageItem>
                    ))}
                  </UI_ImageGrid>
                </Image.PreviewGroup>
              </UI_ImagesSection>
            )}

            {/* Properties Section */}
            <UI_PropertiesSection>
              <UI_Properties_Wrapper>
                {/* Tọa độ nếu có props.lngLat */}
                {hasCoords && props.lngLat && (
                  <UI_Properties_Row>
                    <UI_Properties_Label>
                      <EnvironmentOutlined style={{ color: '#ef4444', fontSize: 13 }} />
                      <span>Tọa độ</span>
                    </UI_Properties_Label>
                    <UI_Properties_Value style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                      <span>
                        {props.lngLat.lat.toFixed(6)}, {props.lngLat.lng.toFixed(6)}
                      </span>
                      <Tooltip title="Sao chép tọa độ">
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined style={{ fontSize: 12, color: '#64748b' }} />}
                          style={{ padding: '0 2px', height: 'auto', lineHeight: 1 }}
                          onClick={handleCopyCoords}
                        />
                      </Tooltip>
                    </UI_Properties_Value>
                  </UI_Properties_Row>
                )}

                {orderedPropertyKeys.map((key, index) => {
                  const fieldDef = fieldsMap[key];
                  const label = getPropertyLabel(key, fieldDef);
                  const rawValue = displayProperties?.[key];
                  return (
                    <UI_Properties_Row key={key || index}>
                      <UI_Properties_Label title={key}>
                        <FieldTypeIcon type={fieldDef?.type} fieldKey={key} />
                        <span>{label}</span>
                      </UI_Properties_Label>
                      <UI_Properties_Value>{renderPropertyValue(key, rawValue, fieldDef)}</UI_Properties_Value>
                    </UI_Properties_Row>
                  );
                })}
              </UI_Properties_Wrapper>
            </UI_PropertiesSection>
          </UI_Body>
        </UI_PanelWrapper>
      </Rnd>

      <CollectFormDetailDrawer
        open={isFormDrawerOpen}
        onClose={() => setIsFormDrawerOpen(false)}
        formUuid={currentFormUuid}
        formData={activeFormData}
        loading={loadingForm}
      />

      {/* Metadata Panel - Shows when preview is open */}
      {previewVisible && currentPreviewImage?.metadata && (
        <UI_MetadataPanel>
          <UI_MetadataPanelTitle>Image Metadata</UI_MetadataPanelTitle>
          {currentPreviewImage.metadata.address && (
            <UI_MetadataSection>
              <UI_MetadataLabel>Address</UI_MetadataLabel>
              <UI_MetadataValue>{currentPreviewImage.metadata.address}</UI_MetadataValue>
            </UI_MetadataSection>
          )}
          {currentPreviewImage.metadata.location?.coords && (
            <>
              <UI_MetadataSection>
                <UI_MetadataLabel>Coordinates</UI_MetadataLabel>
                <UI_MetadataValue>
                  {currentPreviewImage.metadata.location.coords.latitude.toFixed(6)},{' '}
                  {currentPreviewImage.metadata.location.coords.longitude.toFixed(6)}
                </UI_MetadataValue>
              </UI_MetadataSection>
              <UI_MetadataSection>
                <UI_MetadataLabel>Altitude</UI_MetadataLabel>
                <UI_MetadataValue>{currentPreviewImage.metadata.location.coords.altitude?.toFixed(2)} m</UI_MetadataValue>
              </UI_MetadataSection>
              <UI_MetadataSection>
                <UI_MetadataLabel>Accuracy</UI_MetadataLabel>
                <UI_MetadataValue>{currentPreviewImage.metadata.location.coords.accuracy?.toFixed(2)} m</UI_MetadataValue>
              </UI_MetadataSection>
            </>
          )}
          {currentPreviewImage.metadata.compassHeading !== undefined && (
            <UI_MetadataSection>
              <UI_MetadataLabel>Compass Heading</UI_MetadataLabel>
              <UI_MetadataValue>{currentPreviewImage.metadata.compassHeading.toFixed(2)}°</UI_MetadataValue>
            </UI_MetadataSection>
          )}
          {currentPreviewImage.metadata.location?.timestamp && (
            <UI_MetadataSection>
              <UI_MetadataLabel>Timestamp</UI_MetadataLabel>
              <UI_MetadataValue>
                {new Date(currentPreviewImage.metadata.location.timestamp).toLocaleString('vi-VN')}
              </UI_MetadataValue>
            </UI_MetadataSection>
          )}
        </UI_MetadataPanel>
      )}
    </>
  );
};

export default memo(CollectItemPopup);

/* ── Styled Components ─────────────────────────────────────────────────────── */

const UI_PanelWrapper = styled.div`
  width: 350px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const UI_Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  user-select: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const UI_HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const UI_HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const UI_Pager = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 1px 4px;
  margin-right: 2px;
`;

const UI_PagerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }
`;

const UI_PagerText = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #334155;
  font-variant-numeric: tabular-nums;
  padding: 0 3px;
`;

const UI_CloseButton = styled.div`
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background-color: #fee2e2;
    color: #ef4444;
  }
`;

const UI_Body = styled.div`
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: calc(85vh - 80px);
  overflow-y: auto;
`;

const UI_FormBadge = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  max-width: 150px;
  padding: 3px 8px;
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
    color: #1d4ed8;
  }
`;

const UI_Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UI_ImagesSection = styled.div``;

const UI_SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const UI_ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(76px, 1fr));
  gap: 8px;
`;

const UI_ImageItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const UI_ImageWrapper = styled.div`
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  margin-bottom: 3px;
`;

const UI_ImageName = styled.div`
  font-size: 10px;
  color: #64748b;
  text-align: center;
  word-break: break-all;
  line-height: 1.2;
`;

const UI_PropertiesSection = styled.div``;

const UI_Properties_Wrapper = styled.div`
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #f1f5f9;
  border-radius: 6px;
  padding: 4px 8px;
  background: #f8fafc;
`;

const UI_Properties_Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

const UI_Properties_Label = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: #64748b;
  font-weight: 500;
  flex: 1 1 60%;
  min-width: 0;
  min-inline-size: 0;
  display: flex;
  align-items: flex-start;
  gap: 6px;

  > *:first-child {
    margin-top: 2px;
    flex: 0 0 auto;
  }

  > span:last-child {
    flex: 1 1 auto;
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
`;

const UI_Properties_Value = styled.div`
  flex: 0 1 auto;
  min-width: 56px;
  max-width: 44%;
  font-size: 12px;
  line-height: 1.45;
  color: #1e293b;
  text-align: right;
  overflow-wrap: break-word;
  word-break: normal;
`;

const UI_MetadataPanel = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 240px;
  max-height: 400px;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(20px);
  border-radius: 6px;
  padding: 12px;
  z-index: 9999;
  border: 1px solid rgba(255, 255, 255, 0.1);

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    width: 200px;
    bottom: 10px;
    right: 10px;
  }
`;

const UI_MetadataPanelTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  letter-spacing: 0.5px;
`;

const UI_MetadataSection = styled.div`
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const UI_MetadataLabel = styled.div`
  font-size: 9px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const UI_MetadataValue = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.4;
  word-break: break-word;
  font-weight: 400;
`;